import { inject, Injectable, OnDestroy, PLATFORM_ID, signal } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "../../environments/environment";

/**
 * Detecta inactividad del usuario y dispara el logout automático.
 *
 * Flujo:
 *  1. Tras `idleMinutes` sin actividad → muestra modal de advertencia con countdown.
 *  2. Si el usuario presiona "Continuar sesión" → reinicia el timer.
 *  3. Si el countdown llega a 0 sin respuesta → llama al callback de logout.
 *
 * Actividad detectada: mousemove, keydown, click, touchstart, scroll.
 */
@Injectable({ providedIn: "root" })
export class SessionIdleService implements OnDestroy {
  private readonly isBrowser  = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly IDLE_MS    = environment.sessionIdleMinutes * 60 * 1000;
  private readonly WARNING_S  = environment.sessionIdleWarningSeconds;

  readonly showWarning = signal(false);
  readonly countdown   = signal(0);

  private lastActivity  = 0;
  private idleCountdown = 0;
  private tickInterval: ReturnType<typeof setInterval> | null = null;
  private unlisteners:  Array<() => void> = [];
  private onLogout?: () => void;

  // ── API pública ───────────────────────────────────────────

  /** Inicia la detección de inactividad. Llamar tras login o restauración de sesión. */
  start(onLogout: () => void): void {
    if (!this.isBrowser) return;
    this.onLogout    = onLogout;
    this.lastActivity = Date.now();
    this.showWarning.set(false);
    this.clearTick();
    this.registerListeners();
    this.startTick();
  }

  /** Detiene todos los timers y listeners. Llamar en logout. */
  stop(): void {
    this.clearTick();
    this.removeListeners();
    this.showWarning.set(false);
  }

  /** El usuario presionó "Continuar sesión" — reinicia el idle. */
  stayActive(): void {
    this.lastActivity  = Date.now();
    this.idleCountdown = this.WARNING_S;
    this.showWarning.set(false);
  }

  // ── Implementación interna ────────────────────────────────

  private startTick(): void {
    this.idleCountdown = this.WARNING_S;

    this.tickInterval = setInterval(() => {
      const idleMs = Date.now() - this.lastActivity;

      if (!this.showWarning()) {
        if (idleMs >= this.IDLE_MS) {
          this.idleCountdown = this.WARNING_S;
          this.countdown.set(this.idleCountdown);
          this.showWarning.set(true);
        }
      } else {
        this.idleCountdown--;
        this.countdown.set(this.idleCountdown);

        if (this.idleCountdown <= 0) {
          this.stop();
          this.onLogout?.();
        }
      }
    }, 1000);
  }

  private clearTick(): void {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }

  private registerListeners(): void {
    if (!this.isBrowser || this.unlisteners.length > 0) return;

    const handler = () => {
      // Solo actualizar actividad si el modal NO está visible —
      // mientras el modal está abierto el countdown sigue corriendo.
      if (!this.showWarning()) {
        this.lastActivity = Date.now();
      }
    };

    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach(evt => {
      window.addEventListener(evt, handler, { passive: true });
      this.unlisteners.push(() => window.removeEventListener(evt, handler));
    });
  }

  private removeListeners(): void {
    this.unlisteners.forEach(fn => fn());
    this.unlisteners = [];
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
